import type React from "react";
import {useCallback} from "react";
import {useNavigate, useParams} from "react-router-dom";

import {useLevelV2} from "~/api/hooks/useLevelV2";
import {usePlaylist} from "~/api/hooks/usePlaylist";
import CustomButton from "~/component/common/CustomButton";
import MedalDisplay from "~/component/common/MedalDisplay";
import ShareButton from "~/component/common/ShareButton";

import type {PlayLevelParams} from "../PlayLevelParams";
import AddLevelToPlaylist from "../buttons/AddLevelToPlaylist";
import CloneButton from "../buttons/CloneButton";
import LeaderboardButton from "../buttons/LeaderboardButton";
import MainActionButton from "../buttons/MainActionButton";
import NextButton from "../buttons/NextButton";
import PreviousButton from "../buttons/PreviousButton";
import PerformanceInformation from "./PerformanceInformation";

interface PlayMenuContentProps {
  closeModal: () => void;
}

const PlayMenuContent: React.FC<PlayMenuContentProps> = ({closeModal}) => {
  const {id, playlistId} = useParams<PlayLevelParams>();
  const {data: level} = useLevelV2({id});
  const {data: playlistData} = usePlaylist({id: playlistId});
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    navigate(`/playlist/${playlistId}`);
  }, [navigate, playlistId]);

  return (
    <div className="w-full text-white">
      {/* Top Section */}
      <div className="mb-4 text-center">
        <CustomButton
          className="text-lg uppercase tracking-widest text-blue-500"
          onClick={handleClick}
        >
          {playlistData?.name}
        </CustomButton>
        <h2 className="text-3xl">{level?.name ?? "Unknown Level"}</h2>
      </div>

      {/* Performance Information and Medal Display */}
      <MedalDisplay levelId={id} />
      <PerformanceInformation levelId={id} />

      {/* Main Action Button */}
      <MainActionButton closeModal={closeModal} />

      {/* Navigation Section */}
      <div className="flex justify-between items-center mt-4 space-x-2">
        <PreviousButton />
        <CloneButton />
        <ShareButton />
        <LeaderboardButton levelId={id} />
        <AddLevelToPlaylist />
        <NextButton />
      </div>
    </div>
  );
};

export default PlayMenuContent;
